<template>
    <Modal id="pin-request-modal"
           icon="fa-user-lock"
           title="PIN_REQUEST.MODAL_TITLE"
           :cancel="cancel"
           :ok="ok"
           size="sm">
        <p class="text-center">
            {{ l('PIN_REQUEST.PROMPT_LINE_1') }} <strong><em>{{ friendlyPrivilegeName.toLowerCase() }}</em></strong>.
            {{ l('PIN_REQUEST.PROMPT_LINE_2') }}
        </p>

        <NumPad ref="num-pad"
                v-model="numPadValue"
                :show-decimal-button="false"
                :show-sign-button="false"
                mask-value
                is-pin-mode
                @ok="numPadOk"
                @cancel="cancel"
                @clear="onClear" />

        <div v-if="pinOrRfidValid === false"
             class="alert alert-danger">
            <template v-if="lastMethod === 'pin'">
                <i class="fa-solid fa-circle-xmark"></i> {{ l('PIN_REQUEST.INVALID_PIN') }}
            </template>
            <template v-else-if="lastMethod === 'rfid-tag'">
                <i class="fa-solid fa-circle-xmark"></i> {{ l('PIN_REQUEST.INVALID_RFID') }}
            </template>
        </div>
    </Modal>
</template>

<script>
import _ from 'lodash';
import Modal from '@/vue/components/Modals/Modal';
import NumPad from '@/vue/components/NumPad/NumPad';
import { getService } from '@/lib/DI/injector';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    components: { Modal, NumPad },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        privilegeName: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            /**
             * @type {string}
             */
            numPadValue: '',

            /**
             * Human readable description of the permission.
             *
             * @type {string|null}
             */
            friendlyPrivilegeName: null,

            /**
             * Was the last authorization attempt successful?
             *
             * @type {?boolean}
             */
            pinOrRfidValid: null,

            /**
             * Last method ('pin' or 'rfid-tag') that was attempted for authorization.
             *
             * @type {?string}
             */
            lastMethod: null,

            /**
             * Replaced by a debounced function in create().
             */
            debouncedHandleAuthAction: () => {},

            /**
             * Event listener for handling RFID scans. It's store in data so we can remove it when the component
             * is destroyed.
             */
            rfidScanEventListener: null,
        };
    },

    computed: {
    },

    created() {
        /**
         * @type {PrivilegesManager}
         */
        const privilegesManager = getService('privilegesManager');
        this.friendlyPrivilegeName = privilegesManager.generateFriendlyPrivilegeName(this.privilegeName);

        /**
         * Method to attempt to authorise the action via the user identified by the given
         * method and security value (eg PIN and '1122').
         *
         * @param {string} method - 'pin', 'rfid-tag', plus any future values.
         * @param {string} enteredValue The PIN, Tag Id, etc.
         */
        const handleAuthAction = (method, enteredValue) => {
            this.pinOrRfidValid = null;
            this.lastMethod = method;
            privilegesManager.checkUserHasPrivilegeByAuthMethod(method, enteredValue, this.privilegeName)
                .then((result) => {
                    this.pinOrRfidValid = true;
                    this.resolve(result.grantedBy);
                })
                .catch((err) => {
                    this.pinOrRfidValid = false;
                    privilegesManager.log.warn(err);
                });
        };

        this.debouncedHandleAuthAction = _.debounce(handleAuthAction, 100);

        // Listen for RFID tag scan events.
        this.rfidTagScannedEventListener = (event) => {
            console.debug('RFID Tag Scanned', event);

            // Clear a typed-in PIN.
            this.$refs['num-pad'].clear('');
            this.debouncedHandleAuthAction('rfid-tag', event.detail.code);
        };
        document.addEventListener('rfidTagScanned', this.rfidTagScannedEventListener);
    },

    beforeUnmount() {
        document.removeEventListener('rfidTagScanned', this.rfidTagScannedEventListener);
    },

    methods: {
        numPadOk() {
            // The timeout is to allow the RFID handler to pickup the keydowns first if appropriate.
            setTimeout(() => {
                // If an RFID tag has been scanned the numpad value will be empty.
                // So if it's not empty the user must have typed something and pressed enter.
                if (this.numPadValue) {
                    this.debouncedHandleAuthAction('pin', String(this.numPadValue));
                }
            }, 10);
        },

        ok() {
            if (this.numPadValue) {
                this.debouncedHandleAuthAction('pin', String(this.numPadValue));
            }
        },

        onClear() {
            this.pinOrRfidValid = null;
            this.lastMethod = null;
        },
    },
};
</script>

<style lang="less">
@import (reference) "../../../less/variables";

#pin-request-modal {
    p {
        margin-bottom: @gutter-width;
    }

    .alert {
        margin: @gutter-width 0 0 0;
    }
}
</style>
